import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import html2pdf from "html2pdf.js";
import { useDispatch, useSelector } from "react-redux";
import { fetchSalaries } from "../redux/slices/salarySlice";
import { Printer, Download, ArrowLeft } from "lucide-react";
import "../letterstyles/JoiningLetter.css";

const JoiningLetter = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const printRef = useRef(null);
  const dispatch = useDispatch();

  const user = state?.user;
  const { salaryData } = useSelector((state) => state.salary);

  const superAdminId = Number(localStorage.getItem("adminuserId")) || 0;
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      alert("No employee data provided.");
      navigate("/");
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!superAdminId || !user?.id) return;
    dispatch(fetchSalaries({ superAdminId, employeeId: user.id }));
  }, [superAdminId, user?.id, dispatch]);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `https://payyug.in/api/get-company?superAdminId=${superAdminId}`
        );
        const data = await response.json();

        if (response.ok && data) {
          if (data.logo) {
            data.logoUrl = `https://payyug.in/${data.logo.replace(/^images\//, "")}`;
          }
          setCompany(data);
        }
      } catch (err) {
        console.error("Error retrieving company profile:", err);
      } finally {
        setLoading(false);
      }
    };

    if (superAdminId) fetchCompany();
  }, [superAdminId]);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Joining_Letter_${user?.userId || "Employee"}`,
  });

  const handleDownloadPDF = () => {
    const element = printRef.current;
    if (!element) return;

    const opt = {
      margin: 0,
      filename: `Joining_Letter_${user?.userId || "Employee"}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2.5,
        useCORS: true,
        allowTaint: true,
        logging: false,
        scrollY: 0,
      },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait",
      },
      pagebreak: {
        mode: ["avoid-all", "css", "legacy"],
        avoid: [
          ".jl-doc-header",
          ".jl-doc-salary-section",
          ".jl-doc-sign-block",
          ".jl-doc-salary-table tr",
          ".jl-doc-terms-list li",
        ],
      },
    };

    html2pdf().set(opt).from(element).save();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "--";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "--";
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const formatCurrency = (val) => {
    const num = Number(val) || 0;
    return num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  if (!user) return null;
  if (loading) {
    return (
      <div className="jl-doc-loading-screen">
        <p>Loading joining letter...</p>
      </div>
    );
  }

  const salary = Array.isArray(salaryData) ? salaryData[0] : salaryData;
  const basicSalary = Number(salary?.basic || 0);
  const hra = Number(salary?.hra || 0);
  const specialAllowance = Number(salary?.specialAllowance || 0);
  const monthlyGross = Number(salary?.totalSalary) || basicSalary + hra + specialAllowance;
  const annualGross = monthlyGross * 12;

  return (
    <div className="jl-doc-wrapper">
      <div className="jl-doc-action-bar">
        <button
          type="button"
          className="jl-doc-btn jl-doc-btn-back"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={16} /> Back
        </button>

        <div className="jl-doc-action-group">
          <button
            type="button"
            className="jl-doc-btn jl-doc-btn-print"
            onClick={handlePrint}
          >
            <Printer size={16} /> Print Document
          </button>
          <button
            type="button"
            className="jl-doc-btn jl-doc-btn-download"
            onClick={handleDownloadPDF}
          >
            <Download size={16} /> Download PDF
          </button>
        </div>
      </div>

      <div className="jl-doc-sheet-container" ref={printRef}>
        <div className="jl-doc-sheet">
          <header className="jl-doc-header">
            <img
              src={company?.logoUrl || "/default-logo.png"}
              alt="Company Logo"
              className="jl-doc-logo"
            />
            <div className="jl-doc-company-details">
              <h2>{company?.name || ""}</h2>
              <p>
                <strong>Regd. Office:</strong> {company?.address || ""}
              </p>
              <p>
                <strong>CIN:</strong> {company?.cin || "N/A"}
              </p>
              <p>
                <strong>Email:</strong> {company?.email || "—"} |{" "}
                <strong>Contact:</strong> {company?.contact || "—"}
              </p>
            </div>
          </header>

          <div className="jl-doc-title-wrapper">
            <h1>Joining Letter</h1>
          </div>

          <main className="jl-doc-body">
            <div className="jl-doc-date-row">
              <strong>Date:</strong> {formatDate(user.dateOfJoining)}
            </div>

            <div className="jl-doc-recipient-box">
              <p>To,</p>
              <p className="jl-doc-recipient-name">
                <strong>{user.fullName || user.name}</strong>
              </p>
              <p>
                Employee ID: <strong>{user.userId}</strong>
              </p>
              <p>
                Branch / Reporting Location: <strong>{user.branchName || user.address || ""}</strong>
              </p>
            </div>

            <p className="jl-doc-subject-line">
              <strong>Subject:</strong> Formal Joining Letter for the position of{" "}
              <strong>{user.roleName || ""}</strong>
            </p>

            <p>
              <strong>Dear {user.fullName || user.name},</strong>
            </p>

            <p>
              We are pleased to appoint you as <strong>{user.roleName || ""}</strong> at{" "}
              <strong>{company?.name}</strong>. Following our evaluations, we are confident that
              your background will be a valuable asset to our organization.
            </p>

            <p className="jl-doc-terms-title">
              <strong>Key Terms and Conditions of Employment:</strong>
            </p>

            <ol className="jl-doc-terms-list">
              <li>
                <strong>Date of Joining:</strong> Your formal joining takes effect on{" "}
                <strong>{formatDate(user.dateOfJoining)}</strong>.
              </li>
              <li>
                <strong>Place of Work:</strong> Your primary reporting location will be our{" "}
                <strong>{user.branchName || ""}</strong> branch, subject to transfer based on company needs.
              </li>
              <li>
                <strong>Probation Period:</strong> You will serve an initial probationary period of six
                (6) months from your reporting date.
              </li>
              <li>
                <strong>Working Hours:</strong> Standard working hours are 9:30 AM to 6:30 PM, Monday
                through Saturday.
              </li>
              <li>
                <strong>Notice Period:</strong> Either party may terminate this employment by giving
                thirty (30) days' written notice or equivalent compensation in lieu thereof.
              </li>
              <li>
                <strong>Documentation:</strong> Submission of relevant identification, credentials,
                and bank account details is mandatory.
              </li>
            </ol>

            <section className="jl-doc-salary-section jl-doc-no-break">
              <p className="jl-doc-annexure-label">
                <strong>Annexure - A: Compensation & Benefits Schedule</strong>
              </p>

              <table className="jl-doc-salary-table">
                <thead>
                  <tr>
                    <th>Salary Component</th>
                    <th>Monthly Entitlement (₹)</th>
                    <th>Annualized Value (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Basic Salary</td>
                    <td className="jl-doc-num-col">₹{formatCurrency(basicSalary)}</td>
                    <td className="jl-doc-num-col">₹{formatCurrency(basicSalary * 12)}</td>
                  </tr>
                  <tr>
                    <td>House Rent Allowance (H.R.A.)</td>
                    <td className="jl-doc-num-col">₹{formatCurrency(hra)}</td>
                    <td className="jl-doc-num-col">₹{formatCurrency(hra * 12)}</td>
                  </tr>
                  <tr>
                    <td>Special / Performance Allowance</td>
                    <td className="jl-doc-num-col">₹{formatCurrency(specialAllowance)}</td>
                    <td className="jl-doc-num-col">₹{formatCurrency(specialAllowance * 12)}</td>
                  </tr>
                  <tr className="jl-doc-total-row">
                    <td>Gross Total Compensation</td>
                    <td className="jl-doc-num-col">₹{formatCurrency(monthlyGross)}</td>
                    <td className="jl-doc-num-col">₹{formatCurrency(annualGross)}</td>
                  </tr>
                </tbody>
              </table>
              <small className="jl-doc-statutory-note">
                * Applicable statutory deductions including PF, PT, and TDS will be applied as per rules.
              </small>
            </section>

            <div className="jl-doc-sign-block jl-doc-no-break">
              <div className="jl-doc-sign-col">
                <p>
                  <strong>For {company?.name || ""}</strong>
                </p>
                <div className="jl-doc-sign-spacer" />
                <p>
                  <strong>Authorized Signatory</strong>
                </p>
                <p className="jl-doc-sign-sub">Human Resources Department</p>
              </div>

              <div className="jl-doc-sign-col jl-doc-sign-right">
                <p>
                  <strong>Candidate Acceptance</strong>
                </p>
                <div className="jl-doc-sign-spacer" />
                <p>
                  <strong>Signature:</strong> _________________________
                </p>
                <p>
                  <strong>Date:</strong> ______________________________
                </p>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default JoiningLetter;